import { useMemo } from 'react';
import { type AreaTableView, type AreaView } from '../../../../services/areas/areas.service';
import { sortAreasForOperations } from '../../core/pos-shell-page.helpers';

export type UseAreaCanvasPresentationParams = {
  activeAreas: AreaView[];
  settingsAreas: AreaView[];
  selectedAreaId: number | null;
  areaMode: 'view' | 'edit';
  viewCanvasHostWidth: number;
  viewCanvasHostHeight: number;
  editCanvasHostWidth: number;
  editCanvasHostHeight: number;
  viewportWidth: number;
};

export function useAreaCanvasPresentation({
  activeAreas,
  settingsAreas,
  selectedAreaId,
  areaMode,
  viewCanvasHostWidth,
  viewCanvasHostHeight,
  editCanvasHostWidth,
  editCanvasHostHeight,
  viewportWidth,
}: UseAreaCanvasPresentationParams) {
  const orderedActiveAreas = useMemo(() => sortAreasForOperations(activeAreas), [activeAreas]);
  const orderedSettingsAreas = useMemo(() => sortAreasForOperations(settingsAreas), [settingsAreas]);

  const viewArea = useMemo(
    () => orderedActiveAreas.find((area) => area.id === selectedAreaId) ?? orderedActiveAreas[0] ?? null,
    [orderedActiveAreas, selectedAreaId],
  );

  const editArea = useMemo(() => {
    const found = orderedSettingsAreas.find((area) => area.id === selectedAreaId) ?? orderedSettingsAreas[0] ?? null;
    if (!found) {
      return found;
    }
    // Hide soft-deleted tables from the edit canvas (settingsAreas keeps them for numbering).
    return { ...found, tables: found.tables.filter((table) => table.isActive) };
  }, [orderedSettingsAreas, selectedAreaId]);

  const selectedAreaList = areaMode === 'edit' ? orderedSettingsAreas : orderedActiveAreas;

  const viewAreaCanvasBounds = useMemo(() => {
    const areaTables: AreaTableView[] = viewArea?.tables ?? [];
    const fallbackWidth = viewCanvasHostWidth > 0 ? viewCanvasHostWidth : 1320;
    const fallbackHeight = viewCanvasHostHeight > 0 ? viewCanvasHostHeight : 760;
    if (areaTables.length === 0) {
      return { width: fallbackWidth, height: fallbackHeight };
    }

    const maxRight = Math.max(...areaTables.map((table) => table.positionX + table.width));
    const maxBottom = Math.max(...areaTables.map((table) => table.positionY + table.height));
    return {
      width: Math.max(fallbackWidth, maxRight + 48),
      height: Math.max(fallbackHeight, maxBottom + 48),
    };
  }, [viewArea, viewCanvasHostWidth, viewCanvasHostHeight]);

  const editAreaCanvasBounds = useMemo(() => {
    const areaTables: AreaTableView[] = editArea?.tables ?? [];
    const fallbackWidth = editCanvasHostWidth > 0 ? editCanvasHostWidth : 1320;
    const fallbackHeight = editCanvasHostHeight > 0 ? editCanvasHostHeight : 760;
    if (areaTables.length === 0) {
      return { width: fallbackWidth, height: fallbackHeight };
    }

    const maxRight = Math.max(...areaTables.map((table) => table.positionX + table.width));
    const maxBottom = Math.max(...areaTables.map((table) => table.positionY + table.height));
    return {
      width: Math.max(fallbackWidth, maxRight + 48),
      height: Math.max(fallbackHeight, maxBottom + 48),
    };
  }, [editArea, editCanvasHostWidth, editCanvasHostHeight]);

  const isCompactAreaViewport = viewportWidth <= 900;
  const viewAreaCanvasScale = useMemo(() => {
    if (!isCompactAreaViewport || viewCanvasHostWidth <= 0) {
      return 1;
    }
    return Math.min(1, Math.max(0.4, (viewCanvasHostWidth - 4) / viewAreaCanvasBounds.width));
  }, [isCompactAreaViewport, viewCanvasHostWidth, viewAreaCanvasBounds.width]);

  return {
    orderedActiveAreas,
    orderedSettingsAreas,
    viewArea,
    editArea,
    selectedAreaList,
    viewAreaCanvasBounds,
    editAreaCanvasBounds,
    viewAreaCanvasScale,
  };
}
